import React, { useState, useCallback } from 'react';
import { FooterPanel } from './styles';
import ArrowRight from 'img/arrow-right-white.svg';
import ArrowLeft from 'img/arrow-left-white.svg';
import { Button } from 'pages/lectures/styles';
import { LectureQuestion, LectureAnswersState, isQuestionAnswered, isCaseStudyQuestion } from '../../types';
import { getEntryKey } from '../../utils/questionIdentity';
import {
  AlignLeftOutlined,
  PlayCircleOutlined,
  FunctionOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import VideoTypeModal from 'pages/course/components/class/lecture/components/VideoTypeModal';
import LectureVideoQuestion from 'pages/course/components/class/lecture/LectureVideoQuestion.component';
import LectureTextQuestion from 'pages/course/components/class/lecture/LectureTextQuestion.component';
import { FormulaModal } from 'pages/new-questions/components/FormulaModal';
import { Row } from 'pages/new-questions/styles/common';

interface LecturesFooterPanelProps {
  questionSelected: LectureQuestion;
  currentLecture: LectureQuestion[];
  answers: LectureAnswersState;
  isReview?: boolean;
  handleSelectNextQuestion: () => void;
  handleSelectPreviewQuestion: () => void;
  handleFinishLecture: () => void;
  isSimulated?: boolean;
  simulatesWithVideos?: boolean;
  isSubmitting?: boolean;
  /** Navegação interna de steps para questões Estudo de Caso */
  caseStudyStepIndex?: number;
  onCaseStudyNextStep?: () => void;
  onCaseStudyPrevStep?: () => void;
}

export function LecturesFooterPanel({
  questionSelected,
  currentLecture,
  answers,
  isReview = false,
  handleSelectNextQuestion,
  handleSelectPreviewQuestion,
  handleFinishLecture,
  isSimulated = false,
  simulatesWithVideos = false,
  isSubmitting = false,
  caseStudyStepIndex = 0,
  onCaseStudyNextStep,
  onCaseStudyPrevStep,
}: LecturesFooterPanelProps) {
  const [questionTextShow, setQuestionTextShow] = useState(false);
  const [questionVideoStart, setQuestionVideoStart] = useState(false);
  const [modalVideoOption, setModalVideoOption] = useState(false);
  const [isLibrasVideo, setIsLibrasVideo] = useState(false);
  const [formulaModalShow, setFormulaModalShow] = useState(false);

  const screenWidth = window.innerWidth;

  // Posição da questão na prova pela chave da entrada: o mesmo Estudo de Caso pode ocupar
  // várias posições, e comparar por `id_questao` acharia sempre a primeira delas — era o que
  // impedia o botão "Finalizar" de aparecer quando o caso era a última posição.
  const selectedEntryKey = getEntryKey(questionSelected);
  const currentIndex =
    currentLecture?.findIndex(
      (question: LectureQuestion) => getEntryKey(question) === selectedEntryKey,
    ) ?? -1;

  const disabledPreviewButton = currentIndex === 0;

  const isLastQuestion = currentIndex === (currentLecture?.length ?? 0) - 1;

  const unansweredQuestionsCount = currentLecture?.filter(
    (question: LectureQuestion) => !isQuestionAnswered(question, answers),
  ).length;

  const isCurrentQuestionOnlyUnanswered =
    unansweredQuestionsCount === 1 &&
    currentLecture?.some(
      (question: LectureQuestion) =>
        getEntryKey(question) === selectedEntryKey && !isQuestionAnswered(question, answers),
    );

  const disabledNextButton = isReview ? isLastQuestion : false;

  // Questões de Estudo de Caso gerenciam os botões de resolução internamente (por step)
  const isCaseStudy = isCaseStudyQuestion(questionSelected);
  const hasCommentedQuestion = !isCaseStudy && questionSelected?.questao_comentada;
  const hasVideoEmbed = !isCaseStudy && questionSelected?.video_embed;
  const hasFormula = !isCaseStudy && (questionSelected?.formula || questionSelected?.formula_imagem);

  // Para case_study: total de steps e flags de posição
  const caseStudyTotalSteps = isCaseStudy ? questionSelected.step_multiple.length : 1;
  const isFirstCaseStudyStep = isCaseStudy && caseStudyStepIndex === 0;
  const isLastCaseStudyStep = isCaseStudy && caseStudyStepIndex === caseStudyTotalSteps - 1;

  const getNextButtonLabel = () => {
    // Em case_study num step intermediário, o botão sempre avança o step — nunca "Finalizar"
    if (isCaseStudy && !isLastCaseStudyStep) return 'Próximo';
    if (!isLastQuestion || isReview) return 'Próximo';
    return isSubmitting ? 'Finalizando...' : 'Finalizar';
  };

  const getNextButtonIcon = () => {
    if (isSubmitting && isLastQuestion && !isReview) {
      return <LoadingOutlined style={{ marginRight: 6 }} />;
    }
    return <img src={ArrowRight} alt="Próximo" data-testid="lecturesfooterpanel-arrowright" />;
  };

  const handlePlayVideo = useCallback(() => {
    if (questionSelected.video_embed_libras) {
      setModalVideoOption(true);
    } else {
      setQuestionVideoStart(true);
    }
  }, [questionSelected.video_embed_libras]);

  const handlePrevClick = () => {
    if (isCaseStudy && !isFirstCaseStudyStep) {
      onCaseStudyPrevStep?.();
    } else {
      handleSelectPreviewQuestion();
    }
  };

  const handleNextClick = () => {
    if (isCaseStudy && !isLastCaseStudyStep) {
      onCaseStudyNextStep?.();
      return;
    }
    if (!isReview && (isLastQuestion || isCurrentQuestionOnlyUnanswered)) {
      handleFinishLecture();
    } else {
      handleSelectNextQuestion();
    }
  };

  return (
    <FooterPanel data-testid="lecturesfooterpanel-footerpanel">
      <Button
        disabled={disabledPreviewButton && isFirstCaseStudyStep}
        variant="invisible"
        width={screenWidth <= 768 ? '100%' : '130px'}
        onClick={handlePrevClick}
        style={{ display: screenWidth <= 768 ? 'none' : 'flex' }}
        data-testid="lecturesfooterpanel-prev-button-desktop"
      >
        <img src={ArrowLeft} alt="Anterior" data-testid="lecturesfooterpanel-arrowleft-desktop" />
        Anterior
      </Button>

      <Row gap="16px">
        {hasCommentedQuestion && (!isSimulated || isReview) && (
          <Button
            onClick={() => setQuestionTextShow(true)}
            variant="invisible"
            width={screenWidth <= 768 ? '100%' : '130px'}
            minWidth="180px"
          >
            <AlignLeftOutlined /> Ler Resolução
          </Button>
        )}
        {hasVideoEmbed && (!isSimulated || isReview || simulatesWithVideos) && (
          <Button
            onClick={handlePlayVideo}
            variant="invisible"
            width={screenWidth <= 768 ? '100%' : '130px'}
            minWidth="180px"
          >
            <PlayCircleOutlined /> Assistir Resolução
          </Button>
        )}
        {hasFormula && (
          <Button
            onClick={() => setFormulaModalShow(true)}
            variant="invisible"
            width={screenWidth <= 768 ? '100%' : '130px'}
            minWidth="180px"
          >
            <FunctionOutlined /> Fórmula
          </Button>
        )}
      </Row>

      <Button
        disabled={disabledPreviewButton && isFirstCaseStudyStep}
        variant="invisible"
        width={screenWidth <= 768 ? '100%' : '130px'}
        onClick={handlePrevClick}
        style={{ display: screenWidth <= 768 ? 'flex' : 'none' }}
        data-testid="lecturesfooterpanel-prev-button-mobile"
      >
        <img src={ArrowLeft} alt="Anterior" data-testid="lecturesfooterpanel-arrowleft-mobile" />
        Anterior
      </Button>

      <Button
        variant="primary"
        width={screenWidth <= 768 ? '100%' : '130px'}
        disabled={disabledNextButton || isSubmitting}
        aria-busy={isSubmitting || undefined}
        aria-label={isSubmitting ? 'Finalizando prova, aguarde' : undefined}
        onClick={handleNextClick}
        data-testid="lecturesfooterpanel-next-button"
      >
        {getNextButtonLabel()}

        {getNextButtonIcon()}
      </Button>

      {modalVideoOption && (
        <VideoTypeModal
          modalVideoOption={modalVideoOption}
          setModalVideoOption={setModalVideoOption}
          setLSBVideo={setIsLibrasVideo}
          setQuestionVideoStart={setQuestionVideoStart}
        />
      )}
      <LectureVideoQuestion
        questionVideoStart={questionVideoStart}
        setQuestionVideoStart={setQuestionVideoStart}
        videoEmbed={
          questionSelected &&
          (isLibrasVideo ? questionSelected.video_embed_libras : questionSelected.video_embed)
        }
        setModalVideoOption={setModalVideoOption}
        setLSBVideo={setIsLibrasVideo}
      />
      <LectureTextQuestion
        questionTextShow={questionTextShow}
        setQuestionTextShow={setQuestionTextShow}
        text={questionSelected?.questao_comentada}
      />
      {hasFormula && (
        <FormulaModal
          visible={formulaModalShow}
          onClose={() => setFormulaModalShow(false)}
          formula={questionSelected?.formula}
          formulaImagem={questionSelected?.formula_imagem}
        />
      )}
    </FooterPanel>
  );
}
